import { Icon } from "@components/ui/Icon"
import { Tooltip } from "@components/ui/Tooltip"
import { trackEvent } from "@features/tracking/tracking"
import { cn, cx } from "@utils/tw"
import { AnimatePresence, motion, useInView } from "motion/react"
import { memo, useRef } from "react"
import { useKeyPressEvent } from "react-use"

type Props = {
  className?: string
  canGoLeft: boolean
  canGoRight: boolean
  onLeft?: () => void
  onRight?: () => void
  useKeyNav?: boolean
}

export function NavArrows(props: Props) {
  const ref = useRef<HTMLDivElement>(null!)
  const inView = useInView(ref)

  const trackNavigate = () => {
    trackEvent("Navigation Arrows", {}, ref.current)
  }

  useKeyPressEvent("ArrowLeft", (e) => {
    if (props.useKeyNav && inView && props.canGoLeft) {
      e.preventDefault()
      props.onLeft?.()
      trackNavigate()
    }
  })
  useKeyPressEvent("ArrowRight", (e) => {
    if (props.useKeyNav && inView && props.canGoRight) {
      e.preventDefault()
      props.onRight?.()
      trackNavigate()
    }
  })

  return (
    <div ref={ref} className={cn("absolute left-0 right-0 top-1/2 pointer-events-none", props.className)}>
      <div className="absolute left-0 -translate-y-1/2 md:-translate-x-1/3 xxl:-translate-x-1/2">
        <NavArrowButton
          direction="left"
          disabled={!props.canGoLeft}
          onClick={() => {
            props.onLeft?.()
            trackNavigate()
          }}
        />
      </div>
      <div className="absolute right-0 -translate-y-1/2 md:translate-x-1/3 xxl:translate-x-1/2">
        <NavArrowButton
          direction="right"
          disabled={!props.canGoRight}
          onClick={() => {
            props.onRight?.()
            trackNavigate()
          }}
        />
      </div>
    </div>
  )
}

type NavArrowProps = {
  className?: string
  direction: "left" | "right"
  onClick: () => void
  disabled: boolean
}

export const NavArrowButton = memo(function NavArrowButton(props: NavArrowProps) {
  return (
    <AnimatePresence>
      {!props.disabled && (
        <motion.div
          className={cn(props.className)}
          initial={{ scale: 0.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.2, opacity: 0 }}
        >
          <Tooltip
            label={props.direction === "left" ? "Previous" : "Next"}
            side={props.direction === "left" ? "right" : "left"}
            align="center"
          >
            <motion.button
              className={
                "inline-flex items-center border border-black/20 justify-center p-1 bg-bg-mid text-fg/80 pointer-events-auto rounded-md transition-colors active:transition-none active:border-bg-high active:text-fg outline-none"
              }
              onClick={props.onClick}
              disabled={props.disabled}
            >
              <Icon
                name="play"
                className={cx("size-7 md:size-6 lg:size-5 block", props.direction === "left" ? "rotate-180" : "")}
              />
            </motion.button>
          </Tooltip>
        </motion.div>
      )}
    </AnimatePresence>
  )
})
